import {DI_TOKENS} from '@/di/di.tokens';
import {
  AnalyticsClaimRewardDailyDayType,
  AnalyticsClaimRewardDailyRewardType,
} from '@/services/TrackingService/TrackingService.constants';
import {DailyRewardType} from '@/stores/DailyRewardsStore/DailyRewardsStore.constants';

import {injected} from 'brandi';

import {HttpError} from '@specials/project-user';

import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {TrackingService} from '@/services/TrackingService/TrackingService';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {GiftsService} from '@/services/shared/GiftsService/GiftsService';
import {ClickerStateStore} from '@/stores/ClickerStateStore/ClickerStateStore';
import {DailyRewardsStore} from '@/stores/DailyRewardsStore/DailyRewardsStore';
import {GiftsStore} from '@/stores/GiftsStore/GiftsStore';
import {UserStore} from '@/stores/UserStore/UserStore';

export class DailyRewardsService {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly dailyRewardsStore: DailyRewardsStore,
    private readonly loggerService: LoggerService,
    private readonly userStore: UserStore,
    private readonly giftsStore: GiftsStore,
    private readonly clickerStateStore: ClickerStateStore,
    private readonly giftsService: GiftsService,
    private readonly trackingService: TrackingService,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
  ) {}

  public async fetchDailyRewards(): Promise<void> {
    try {
      const resp = await this.projectApi.fetchDailyRewards();

      this.dailyRewardsStore.setRewards(
        resp.payload.rewards.map((r, idx, rewards) => {
          const previousReward = rewards[idx - 1];
          const isCurrent = (!previousReward || rewards[idx - 1].wasReceived) && !r.wasReceived;

          return {
            ...r,
            isGift: r.reward.type === DailyRewardType.Gift,
            isCurrent,
          };
        }),
      );
    } catch (e) {
      this.loggerService.logHttpError(e as HttpError);
      this.setIsAvailableDailyRewardFalse();

      if (this.errorsService.isErrorRecalculationLeagues(e)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      }
    }
  }

  public async claimReward(): Promise<void> {
    const rewards = this.dailyRewardsStore.getRewards();
    const wasNotReceivedReward = rewards.find(({wasReceived}) => !wasReceived);

    try {
      let wasReceived = false;
      this.dailyRewardsStore.setLoadingFetchStatus();

      this.dailyRewardsStore.setRewards(
        rewards.map((r) => {
          if (!r.wasReceived && !wasReceived) {
            wasReceived = true;

            return {...r, wasReceived: true};
          }

          return r;
        }),
      );

      await this.projectApi.claimDailyReward();

      if (wasNotReceivedReward) {
        let trackingDayType: AnalyticsClaimRewardDailyDayType;
        if (wasNotReceivedReward.day === 1) {
          trackingDayType = AnalyticsClaimRewardDailyDayType.FirstDay;
        } else if (wasNotReceivedReward.day === 2) {
          trackingDayType = AnalyticsClaimRewardDailyDayType.SecondDay;
        } else if (wasNotReceivedReward.day === 3) {
          trackingDayType = AnalyticsClaimRewardDailyDayType.ThirdDay;
        } else {
          trackingDayType = AnalyticsClaimRewardDailyDayType.FourthDay;
        }

        const trackingRewardType = wasNotReceivedReward.isGift
          ? AnalyticsClaimRewardDailyRewardType.ProductReward
          : AnalyticsClaimRewardDailyRewardType.InGameReward;

        this.trackingService.trackClaimRewardDaily(trackingDayType, trackingRewardType);
      }

      this.dailyRewardsStore.setIdleFetchStatus();
      this.dailyRewardsStore.setRewardClaimed();
    } catch (e) {
      this.loggerService.logHttpError(e as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(e)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.dailyRewardsStore.setClaimRewardError(e);
        this.dailyRewardsStore.setErrorFetchStatus();
        this.dailyRewardsStore.setRewards(rewards);

        throw e;
      }
    }
  }

  public prefetchGifts(): void {
    void this.giftsService.fetchGifts();
  }

  public updateGifts = (): void => {
    this.giftsStore.setHasNewGifts(true);
  };

  public setIsAvailableDailyRewardFalse(): void {
    this.userStore.setIsAvailableDailyReward(false);
  }

  public onHideClaimRewardError(): void {
    this.dailyRewardsStore.setIdleFetchStatus();
    this.dailyRewardsStore.setClaimRewardError(null);
  }

  public updateCrystalReward = (): void => {
    const reward = this.dailyRewardsStore.getCurrentReward();

    if (reward && reward.reward.type === DailyRewardType.Gems) {
      this.clickerStateStore.addPoints(reward.reward.value);
    }
  };

  public updateRechargesCountReward = (): void => {
    const reward = this.dailyRewardsStore.getCurrentReward();

    if (reward && reward.reward.type === DailyRewardType.EnergyRecharge) {
      this.clickerStateStore.addEnergyChargeCount(reward.reward.value);
    }
  };
}

injected(
  DailyRewardsService,
  DI_TOKENS.projectApi,
  DI_TOKENS.dailyRewardsStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.userStore,
  DI_TOKENS.giftsStore,
  DI_TOKENS.clickerStateStore,
  DI_TOKENS.giftsService,
  DI_TOKENS.trackingService,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
);
